import { useState, useCallback } from 'react';
import { toast } from '../lib/toast';
import i18n from '../i18n/i18n';
import { compressImage } from '../utils/compressImage';

/**
 * Custom hook that manages all menu-item form state (add + edit).
 * Extracts 15+ useState variables from Admin.js to reduce complexity.
 *
 * @returns {object} Form state, setters, and utility methods
 */
export default function useMenuItemForm() {
  // ---- Add form ----
  const [itemName, setItemName] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemPrice, setItemPrice] = useState('');
  const [itemSection, setItemSection] = useState('');
  const [itemCategory, setItemCategory] = useState('');
  const [itemImage, setItemImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // ---- Edit form ----
  const [editingItem, setEditingItem] = useState(null);
  const [editItemName, setEditItemName] = useState('');
  const [editItemDescription, setEditItemDescription] = useState('');
  const [editItemPrice, setEditItemPrice] = useState('');
  const [editItemSection, setEditItemSection] = useState('');
  const [editItemCategory, setEditItemCategory] = useState('');
  const [editItemImage, setEditItemImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  // v2 fields
  const [editItemCost, setEditItemCost] = useState('');
  const [editItemIsPopular, setEditItemIsPopular] = useState(false);
  const [editItemSizes, setEditItemSizes] = useState([]);

  // ---- Edit translations (es / ar — en is driven by editItemName/editItemDescription) ----
  const [editTranslations, setEditTranslations] = useState({
    en: { name: '', description: '', isAutoTranslated: false },
    es: { name: '', description: '', isAutoTranslated: false },
    ar: { name: '', description: '', isAutoTranslated: false },
  });
  const [editActiveLangTab, setEditActiveLangTab] = useState('en');
  const [editTranslating, setEditTranslating] = useState(false);

  /** Reset all add-form fields */
  const resetAddForm = useCallback(() => {
    setItemName('');
    setItemDescription('');
    setItemPrice('');
    setItemSection('');
    setItemCategory('');
    setItemImage(null);
    setImagePreview(null);
  }, []);

  /** Reset all edit-form fields and clear editingItem */
  const resetEditForm = useCallback(() => {
    setEditingItem(null);
    setEditItemName('');
    setEditItemDescription('');
    setEditItemPrice('');
    setEditItemSection('');
    setEditItemCategory('');
    setEditItemImage(null);
    setEditImagePreview(null);
    setEditItemCost('');
    setEditItemIsPopular(false);
    setEditItemSizes([]);
    setEditTranslations({
      en: { name: '', description: '', isAutoTranslated: false },
      es: { name: '', description: '', isAutoTranslated: false },
      ar: { name: '', description: '', isAutoTranslated: false },
    });
    setEditActiveLangTab('en');
  }, []);

  /** Populate the edit form from a menu item object */
  const populateEditForm = useCallback((item) => {
    setEditingItem(item);
    setEditItemName(item.name || '');
    setEditItemDescription(item.description || '');
    setEditItemPrice(item.price != null ? String(item.price) : '');
    setEditItemSection(item.section || '');
    setEditItemCategory(item.category || '');
    setEditItemImage(null);
    setEditImagePreview(item.image_url || null);
    setEditItemCost(item.cost != null ? String(item.cost) : '');
    setEditItemIsPopular(!!item.is_popular);
    setEditItemSizes(Array.isArray(item.sizes) ? item.sizes.map(s => ({ ...s })) : []);
    setEditActiveLangTab('en');
  }, []);

  /** Update a single field for a single language in the edit translation state */
  const updateEditTranslation = useCallback((langCode, field, value) => {
    setEditTranslations(prev => ({
      ...prev,
      [langCode]: {
        ...prev[langCode],
        [field]: value,
        isAutoTranslated: field === 'isAutoTranslated' ? value : false,
      },
    }));
  }, []);

  /** Hydrate the edit translation state from the array returned by GET /api/menu-items/:id/translations */
  const setEditTranslationsFromArray = useCallback((rows) => {
    const next = {
      en: { name: '', description: '', isAutoTranslated: false },
      es: { name: '', description: '', isAutoTranslated: false },
      ar: { name: '', description: '', isAutoTranslated: false },
    };
    for (const row of rows || []) {
      if (!next[row.language_code]) continue;
      next[row.language_code] = {
        name: row.name || '',
        description: row.description || '',
        isAutoTranslated: !!row.is_auto_translated,
      };
    }
    setEditTranslations(next);
  }, []);

  /** Handle file input change for the add form */
  const handleImageChange = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      // Downscale + recompress in the browser so the base64 payload stays under
      // the server's 10 MB limit (prevents 413 on large photos).
      const { dataUrl, file: compressed } = await compressImage(file);
      setItemImage(compressed);
      setImagePreview(dataUrl);
    } catch (err) {
      toast.error(i18n.t('admin.addMenuItem.imageUnreadable'));
      e.target.value = '';
    }
  }, []);

  /** Handle file input change for the edit form */
  const handleEditImageChange = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const { dataUrl, file: compressed } = await compressImage(file);
      setEditItemImage(compressed);
      setEditImagePreview(dataUrl);
    } catch (err) {
      toast.error(i18n.t('admin.addMenuItem.imageUnreadable'));
      e.target.value = '';
    }
  }, []);

  return {
    // Add form state
    itemName, setItemName,
    itemDescription, setItemDescription,
    itemPrice, setItemPrice,
    itemSection, setItemSection,
    itemCategory, setItemCategory,
    itemImage, setItemImage,
    imagePreview, setImagePreview,
    // Edit form state
    editingItem, setEditingItem,
    editItemName, setEditItemName,
    editItemDescription, setEditItemDescription,
    editItemPrice, setEditItemPrice,
    editItemSection, setEditItemSection,
    editItemCategory, setEditItemCategory,
    editItemImage, setEditItemImage,
    editImagePreview, setEditImagePreview,
    // v2 edit fields
    editItemCost, setEditItemCost,
    editItemIsPopular, setEditItemIsPopular,
    editItemSizes, setEditItemSizes,
    // Edit translation state
    editTranslations, setEditTranslations,
    editActiveLangTab, setEditActiveLangTab,
    editTranslating, setEditTranslating,
    updateEditTranslation,
    setEditTranslationsFromArray,
    // Utilities
    resetAddForm,
    resetEditForm,
    populateEditForm,
    handleImageChange,
    handleEditImageChange,
  };
}
